'use client';

import { AUTH_PROVIDER, SESSION_STATUS } from '@/constants/auth';
import { SEARCH_PARAM, SEARCH_PARAM_VALUE } from '@/constants/searchParams';
import { SOCIETY_SETTING_TYPE } from '@/constants/societySettings';
import { useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useSession, signIn } from 'next-auth/react';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import {
  useI4LinkStatus,
  useI4Downloads,
  useUnlinkI4Account,
  useI4LinkUrl,
} from '@/publicSearch/graphql/i4/hooks';
import type { DocumentDto } from '@/publicSearch/graphql/i4/types';
import { Typography } from '@/primitives';
import { ROUTE_SEGMENT } from '@/constants/routeSegments';

const IMAGE_EXTENSIONS = new Set([
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.bmp',
  '.svg',
  '.tiff',
  '.tif',
]);

function isImageFile(fileName: string | null | undefined): boolean {
  if (!fileName) return false;
  const ext = fileName.slice(fileName.lastIndexOf('.')).toLowerCase();
  return IMAGE_EXTENSIONS.has(ext);
}

function buildProxyUrl(
  doc: DocumentDto,
  societyId: number | undefined,
  download: boolean,
) {
  if (!doc.url) return null;
  const params = new URLSearchParams({
    url: doc.url,
    societyId: String(societyId ?? ''),
  });
  if (download) {
    params.set(SEARCH_PARAM.DOWNLOAD, SEARCH_PARAM_VALUE.TRUE);
    if (doc.fileName) params.set(SEARCH_PARAM.FILENAME, doc.fileName);
  }
  return `/api/document?${params.toString()}`;
}

function ImageThumb({ src, alt }: { src: string; alt: string }) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <Box
        component="img"
        src={src}
        alt={alt}
        onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
        onMouseLeave={() => setAnchorEl(null)}
        sx={{
          width: 48,
          height: 48,
          objectFit: 'cover',
          borderRadius: 1,
          cursor: 'pointer',
        }}
      />
      <Popover
        sx={{ pointerEvents: 'none' }}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        onClose={() => setAnchorEl(null)}
        disableRestoreFocus
      >
        <Box
          component="img"
          src={src}
          alt={alt}
          sx={{ display: 'block', maxWidth: '30vw', height: 'auto' }}
        />
      </Popover>
    </>
  );
}

/**
 * Downloads tab content.
 * - Linked users: see their member-scoped documents fetched directly from i6.
 * - Unlinked authenticated users: see CTA to link via landing page (section 2).
 * - Unauthenticated users: redirect to sign-in.
 */
export default function DownloadsPage() {
  const society = usePublicSearchContext((c) => c.society);
  const { status } = useSession();
  const pathname = usePathname();
  const router = useRouter();

  const societyCode = society.societyCode ?? '';
  const isAuthenticated = status === SESSION_STATUS.AUTHENTICATED;

  const onlineRegSetting = society.settings?.find(
    (s) =>
      s.societySettingType === SOCIETY_SETTING_TYPE.ONLINE_REGISTRATIONS_TAB,
  );
  const isILROnline =
    onlineRegSetting?.onlineRegistrationProvider?.trim()?.toUpperCase() ===
    'ILRONLINE';

  // Redirect unauthenticated users to login with callback back to this tab (section 1).
  // Defer signIn so SessionProvider updates don't run during this component's mount commit.
  useEffect(() => {
    if (status !== SESSION_STATUS.UNAUTHENTICATED || !societyCode) return;
    const id = window.setTimeout(() => {
      const orgCode = pathname.split('/')[1] || society.societyCode || '';
      const callbackUrl = `/${orgCode}/${ROUTE_SEGMENT.EXTERNAL}/${ROUTE_SEGMENT.DOWNLOADS}`;
      void signIn(AUTH_PROVIDER, { callbackUrl });
    }, 0);
    return () => clearTimeout(id);
  }, [status, pathname, society.societyCode, societyCode]);

  const {
    data: statusData,
    loading: statusLoading,
    refetch: refetchStatus,
  } = useI4LinkStatus(societyCode, !isAuthenticated || !societyCode);

  const linkStatus = statusData?.i4LinkStatus ?? null;
  const isLinked = !!linkStatus;

  const {
    data: downloadsData,
    loading: downloadsLoading,
    error: downloadsError,
  } = useI4Downloads(societyCode, !isLinked || !isAuthenticated);

  // Prefetch link URL when not linked so "Start" on link-i4 page is instant
  const linkCallbackPath = isILROnline
    ? '/account/linked-accounts/ilronline-callback'
    : '/account/linked-accounts/i4-callback';
  const linkCallbackUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${linkCallbackPath}`
      : '';
  useI4LinkUrl(
    { societyCode, callbackUrl: linkCallbackUrl },
    !isAuthenticated || !societyCode || isLinked || !linkCallbackUrl,
  );

  const [unlinkI4Account, { loading: unlinkLoading }] = useUnlinkI4Account();

  const handleUnlink = async () => {
    await unlinkI4Account({ variables: { input: { societyCode } } });
    refetchStatus();
  };

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          p: 3,
        }}
      >
        <Alert severity="info">Please sign in to access your downloads.</Alert>
      </Box>
    );
  }

  if (statusLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          p: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
      }}
    >
      {isLinked && linkStatus ? (
        <Stack spacing={2}>
          <Stack
            direction="row"
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              Showing downloads for <strong>{linkStatus.memberIdent}</strong>
            </Typography>
            <Button
              size="small"
              startIcon={<LinkOffIcon />}
              onClick={handleUnlink}
              disabled={unlinkLoading}
              color="warning"
            >
              Unlink i4 Account
            </Button>
          </Stack>
          <Divider />

          {downloadsLoading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                p: 3,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {downloadsError && (
            <Alert severity="error">
              Failed to load downloads. Please try again.
            </Alert>
          )}

          {!downloadsLoading &&
            !downloadsError &&
            (downloadsData?.i4Downloads?.length ?? 0) === 0 && (
              <Typography
                sx={{
                  color: 'text.secondary',
                  textAlign: 'center',
                  py: 3,
                }}
              >
                No downloads found for your account.
              </Typography>
            )}

          {!downloadsLoading &&
            !downloadsError &&
            downloadsData?.i4Downloads && (
              <List dense>
                {downloadsData.i4Downloads.map((doc: DocumentDto) => {
                  const downloadUrl = buildProxyUrl(
                    doc,
                    society.societyId,
                    true,
                  );
                  const isImage = isImageFile(doc.fileName);
                  const thumbUrl = isImage
                    ? buildProxyUrl(doc, society.societyId, false)
                    : null;
                  return (
                    <ListItem
                      key={doc.documentGuid}
                      secondaryAction={
                        <Button
                          size="small"
                          startIcon={<DownloadIcon />}
                          href={downloadUrl ?? '#'}
                          disabled={!downloadUrl}
                        >
                          Download
                        </Button>
                      }
                    >
                      <ListItemIcon>
                        {thumbUrl ? (
                          <ImageThumb src={thumbUrl} alt={doc.fileName ?? ''} />
                        ) : (
                          <DescriptionIcon />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={doc.fileName ?? 'Document'}
                        secondary={
                          doc.documentSize
                            ? `${(doc.documentSize / 1024).toFixed(1)} KB`
                            : undefined
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
        </Stack>
      ) : (
        <Stack
          spacing={2}
          sx={{
            alignItems: 'center',
            py: 4,
          }}
        >
          <Typography>
            Link your Internet Solutions account to view your downloads directly
            in BreedLOGIC.
          </Typography>
          <Button
            variant="contained"
            onClick={() =>
              router.push(
                `/${pathname.split('/')[1] || society.societyCode || ''}/${ROUTE_SEGMENT.EXTERNAL}/link-i4`,
              )
            }
          >
            Link my Internet Solutions account
          </Button>
        </Stack>
      )}
    </Box>
  );
}
